import React from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Group,
  NumberInput,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from '@cowbell/ui/core';
import { DatePickerInput } from '@cowbell/ui/dates';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import type { ArrayControl, Control, GroupControl } from './types';

// Above the endorsement modal (zIndex 1400) so the calendar popover / tooltips
// aren't trapped behind the modal overlay.
const DATE_POPOVER_Z_INDEX = 1500;

// Mantine's label slot is fit-content by default, so it ignores a narrow input
// column and lets long labels bleed into neighbouring fields. Bounding it to the
// input width lets FieldLabel's ellipsis + tooltip do their job.
const BOUNDED_LABEL_STYLES = {
  label: { display: 'block', maxWidth: '100%' },
} as const;

// Single-line label that ellipsises when too long. Only when the text actually
// overflows do we show an info icon hint + a tooltip of the full label, so long
// field names don't wrap and blow out the row height.
function FieldLabel({ label }: { readonly label: string }) {
  const textRef = React.useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useLayoutEffect(() => {
    const element = textRef.current;
    if (!element) {
      return undefined;
    }
    const measure = () =>
      setIsTruncated(element.scrollWidth > element.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [label]);

  return (
    <Tooltip
      label={label}
      multiline
      w={240}
      withinPortal
      zIndex={DATE_POPOVER_Z_INDEX}
      disabled={!isTruncated}
    >
      <Group gap={4} wrap="nowrap" style={{ maxWidth: '100%' }}>
        <Text
          ref={textRef}
          size="sm"
          truncate="end"
          style={{ flex: 1, minWidth: 0 }}
        >
          {label}
        </Text>
        {isTruncated && (
          <FontAwesomeIcon
            icon={faCircleInfo}
            style={{ fontSize: '0.7rem', flexShrink: 0, opacity: 0.6 }}
          />
        )}
      </Group>
    </Tooltip>
  );
}

interface FactoryControlProps {
  readonly control: Control;
  readonly namePrefix?: string;
}

// Renders one schema-derived control via Mantine, wired through react-hook-form.
// Presentation only: the control shape comes from the pure factory core.
export function FactoryControl({ control, namePrefix }: FactoryControlProps) {
  const name = namePrefix
    ? `${namePrefix}.${control.fieldKey}`
    : control.fieldKey;
  const { control: formControl } = useFormContext();

  if (control.kind === 'array') {
    return <ArrayControlField control={control} name={name} />;
  }

  if (control.kind === 'group') {
    return <GroupControlField control={control} name={name} />;
  }

  return (
    <Controller
      name={name}
      control={formControl}
      render={({ field, fieldState }) => {
        const error = fieldState.error?.message;
        if (control.kind === 'number') {
          return (
            <NumberInput
              label={<FieldLabel label={control.label} />}
              styles={BOUNDED_LABEL_STYLES}
              thousandSeparator={control.currency ? ',' : undefined}
              value={field.value as number | string}
              onChange={field.onChange}
              onBlur={field.onBlur}
              min={control.minimum}
              max={control.maximum}
              error={error}
            />
          );
        }
        if (control.kind === 'toggle') {
          return (
            <Switch
              label={control.label}
              checked={Boolean(field.value)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                field.onChange(event.currentTarget.checked)
              }
              onBlur={field.onBlur}
              error={error}
            />
          );
        }
        if (control.kind === 'date') {
          return (
            <DatePickerInput
              label={<FieldLabel label={control.label} />}
              styles={BOUNDED_LABEL_STYLES}
              value={field.value as string | null}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={error}
              clearable
              popoverProps={{
                withinPortal: true,
                zIndex: DATE_POPOVER_Z_INDEX,
              }}
            />
          );
        }
        return (
          <TextInput
            label={<FieldLabel label={control.label} />}
            styles={BOUNDED_LABEL_STYLES}
            value={(field.value as string) ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            error={error}
          />
        );
      }}
    />
  );
}

interface GroupControlFieldProps {
  readonly control: GroupControl;
  readonly name: string;
}

// A single object of fields rendered as a titled row — the array row layout
// without add/remove. Child names nest under the group's own name so the form
// value is an object keyed by each child fieldKey.
function GroupControlField({ control, name }: GroupControlFieldProps) {
  return (
    <Stack gap="xs">
      <Text fw={600}>{control.label}</Text>
      <Group align="flex-end" wrap="nowrap">
        {control.fields.map((field) => (
          <Box key={field.fieldKey} style={{ flex: 1, minWidth: 0 }}>
            <FactoryControl control={field} namePrefix={name} />
          </Box>
        ))}
      </Group>
    </Stack>
  );
}

interface ArrayControlFieldProps {
  readonly control: ArrayControl;
  readonly name: string;
}

function emptyRow(itemFields: Control[]): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  for (const field of itemFields) {
    if (field.kind === 'toggle') {
      row[field.fieldKey] = false;
    } else if (field.kind === 'array') {
      row[field.fieldKey] = [];
    } else {
      row[field.fieldKey] = '';
    }
  }
  return row;
}

function ArrayControlField({ control, name }: ArrayControlFieldProps) {
  const { control: formControl } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control: formControl,
    name,
  });
  const atMax =
    control.maxItems !== undefined && fields.length >= control.maxItems;

  return (
    <Stack gap="xs">
      <Text fw={600}>{control.label}</Text>
      {fields.map((row, index) => (
        <Group key={row.id} align="flex-end" wrap="nowrap">
          {control.itemFields.map((itemField) => (
            <Box key={itemField.fieldKey} style={{ flex: 1, minWidth: 0 }}>
              <FactoryControl
                control={itemField}
                namePrefix={`${name}.${index}`}
              />
            </Box>
          ))}
          <ActionIcon
            variant="subtle"
            color="red"
            aria-label={`Remove ${control.label} row`}
            onClick={() => remove(index)}
          >
            ✕
          </ActionIcon>
        </Group>
      ))}
      <Button
        variant="light"
        onClick={() => append(emptyRow(control.itemFields))}
        disabled={atMax}
      >
        Add {control.label}
      </Button>
      {atMax && (
        <Text size="xs" c="dimmed">
          Maximum of {control.maxItems} rows reached.
        </Text>
      )}
    </Stack>
  );
}
